import { Fragment, useMemo, type ReactNode } from "react";
import {
  isNodeVisible,
  resolveNodeSettings,
  type SectionNode,
  type ThemeRegion,
} from "@workspace/theme-engine";
import { useGetSettings } from "@workspace/api-client-react";
import { useThemeEngine, type ActiveEngineTheme } from "./context";
import { getRenderer } from "./registry";

/**
 * Renders one storefront region (header/footer/home/product/listing/cart/
 * customPage) from the active engine theme's layout tree. Returns `fallback`
 * when the engine is disabled or the region is empty, so callers can always
 * fall back to the legacy chrome.
 */
export function RegionRenderer({
  region,
  pageContent,
  fallback = null,
}: {
  region: ThemeRegion;
  pageContent?: ReactNode;
  fallback?: ReactNode;
}) {
  const theme = useThemeEngine();
  const { data: storeSettings } = useGetSettings();

  // Data context exposed to sandboxed templates. Values are HTML-escaped by the
  // interpreter and re-sanitized by DOMPurify, so passing store metadata here is
  // safe — there is no path for it to become active content.
  const templateContext = useMemo<Record<string, unknown>>(() => {
    const s = (storeSettings ?? {}) as Record<string, unknown>;
    return {
      settings: theme?.settings ?? {},
      store: {
        name: typeof s.storeName === "string" ? s.storeName : "",
        tagline:
          typeof s.storeTagline === "string"
            ? s.storeTagline
            : typeof s.storeDescription === "string"
              ? s.storeDescription
              : "",
      },
    };
  }, [theme?.settings, storeSettings]);

  if (!theme) return <>{fallback}</>;
  const nodes = (theme.layout as Record<string, SectionNode[]>)[region];
  if (!Array.isArray(nodes) || nodes.length === 0) return <>{fallback}</>;
  return <>{renderNodes(nodes, theme, templateContext, pageContent)}</>;
}

function renderNodes(
  nodes: SectionNode[],
  theme: ActiveEngineTheme,
  templateContext: Record<string, unknown>,
  pageContent: ReactNode,
): ReactNode {
  return nodes.map((node, idx) => {
    // Show/hide: skip nodes whose `visible` (boolean or `$setting:` ref)
    // evaluates to false against the merged settings.
    if (!isNodeVisible(node, theme.settings)) return null;
    const renderer = getRenderer(node.component);
    if (!renderer) return null;
    const settings = resolveNodeSettings(node, theme.settings);
    const childrenNodes = collectChildren(node, theme, templateContext, pageContent);
    return (
      <Fragment key={node.id || idx}>
        {renderer({
          node,
          children: childrenNodes,
          pageContent,
          settings,
          templates: theme.templates,
          templateContext,
        })}
      </Fragment>
    );
  });
}

function collectChildren(
  node: SectionNode,
  theme: ActiveEngineTheme,
  templateContext: Record<string, unknown>,
  pageContent: ReactNode,
): ReactNode {
  if (!node.slots) return null;
  const out: ReactNode[] = [];
  for (const [slotName, children] of Object.entries(node.slots)) {
    if (Array.isArray(children) && children.length > 0) {
      out.push(
        <Fragment key={slotName}>
          {renderNodes(children, theme, templateContext, pageContent)}
        </Fragment>,
      );
    }
  }
  return out.length ? <>{out}</> : null;
}
